// CalendarFieldColor.js

import { isThemePaletteTokenId } from 'context/theme/themeTokenSchema';
import { isTemporalFieldType } from 'views/utils/temporalFieldFormat';

/**
 * Normalizes a schema field calendar color into a named theme palette token id.
 *
 * Colors are stored as token ids (for example `blue`) rather than hex values so
 * each theme mode resolves its own shade at render time.
 *
 * @param {unknown} value - Raw stored or drafted color value.
 *
 * @returns {string} A palette token id, or an empty string when unset or unrecognized.
 */
export function normalizeCalendarColor(value) {
  if (typeof value !== 'string') return '';

  const normalized = value.trim().toLowerCase();
  if (!normalized) return '';

  return isThemePaletteTokenId(normalized) ? normalized : '';
}

/**
 * Reads the calendar color token from a schema field definition.
 *
 * @param {object} field - Schema field definition.
 *
 * @returns {string} A palette token id, or an empty string when the field has none.
 */
export function getFieldCalendarColor(field) {
  if (!field || typeof field !== 'object') return '';
  if (!isTemporalFieldType(field.type)) return '';

  return normalizeCalendarColor(field.calendarColor);
}

/**
 * Resolves a palette token id to a concrete color in the active theme mode.
 *
 * @param {object} theme - Resolved theme object from useTheme().
 * @param {string} tokenId - Named palette token id.
 *
 * @returns {string} The theme color, or an empty string when unresolvable.
 */
export function resolveThemePaletteColor(theme, tokenId) {
  const normalized = normalizeCalendarColor(tokenId);
  if (!normalized || !theme) return '';

  const color = theme[normalized];

  return typeof color === 'string' && color.trim() ? color.trim() : '';
}
